import { createPortal } from "react-dom";
import { Clock } from "lucide-react";

import Button from "@/components/ui/Button.jsx";
import { cx } from "@/lib/cx.js";

/*
 * The sticky save bar, from settings.html.
 *
 * It slides in only once something is dirty, and it NAMES what is dirty. That
 * second part is the point: a settings page where every field is live is much
 * quicker to use than the modal-per-section it replaces, but only if you can
 * still see what you are about to commit before you commit it.
 */
export default function SaveBar({ open, labels, saving, onSave, onDiscard }) {
    return createPortal(
        <div
            data-save-bar={open ? "open" : "closed"}
            className={cx(
                "pointer-events-none fixed inset-x-0 bottom-0 z-50 p-4 transition-transform duration-200",
                open ? "translate-y-0" : "translate-y-full",
            )}
            aria-hidden={!open}
        >
            <div className="pointer-events-auto mx-auto flex max-w-3xl flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400">
                    <Clock className="size-4.5" strokeWidth={2} />
                </span>

                <div className="min-w-0">
                    <p data-save-count className="text-[13px] font-semibold">
                        {labels.length} unsaved change{labels.length === 1 ? "" : "s"}
                    </p>
                    <p
                        data-save-list
                        title={labels.join(", ")}
                        className="truncate text-[11.5px] text-slate-400 dark:text-slate-500"
                    >
                        {labels.join(" · ")}
                    </p>
                </div>

                <div className="ml-auto flex items-center gap-2.5">
                    <Button onClick={onDiscard} disabled={saving} data-discard>
                        Discard
                    </Button>
                    <Button
                        variant="primary"
                        onClick={onSave}
                        loading={saving}
                        loadingLabel="Saving…"
                        data-save
                    >
                        Save changes
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
